import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import type { Environment } from "react-relay";
import type { SdkFunction } from "scmp-sdk";

import { sendMirrorCustomEvent } from "shared/components/tracking/mirror";
import { config, newsletter } from "shared/data";
import { useCurrentPageType } from "shared/hooks";
import { accountAtom } from "shared/lib/account";
import { useEdition } from "shared/lib/edition";
import { useGetHomeAsPath, useRouterReplaceWithSanitizedKeys } from "shared/lib/router";

import { gdprAtom } from "scmp-app/components/gdpr-banner/atoms";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { sendGTMTracking } from "scmp-app/components/tracking/google-tag-manager/apis";
import { appAtom } from "scmp-app/lib/app/atoms";
import { useCurrentArticleIds } from "scmp-app/lib/current-item";
import { useFetchUserTypes, useMyNews } from "scmp-app/lib/hooks";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import { useRouteHandler } from "scmp-app/lib/router/hooks";
import { useUserService } from "scmp-app/lib/user-service/hooks";

import { useHasWhyRegisterPPTOHomepagePositionIntersected } from "./has-why-register-ppto-homepage-position-intersected";

export const useScmpSdk = (environment: Environment) => {
  const { asPath, pathname, query } = useRouter();
  const router = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();
  const handleRoutePush_ = useRouteHandler();
  const { currentArticleId, currentArticleUuid } = useCurrentArticleIds();

  const edition = useEdition();
  const currentPageType = useCurrentPageType();
  const { bookmarkAction, checkIsBookmarked, checkIsFollowed, followAction } = useUserService();
  const { hasEnoughFollowedItems, initialHasEnoughFollowedItems } = useMyNews();
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { fetchUserTypes, userTypes } = useFetchUserTypes();
  const userTypeIds = userTypes.map(userType => userType.id);
  useEffect(() => {
    void fetchUserTypes();
  }, [fetchUserTypes]);

  const { navigatorInfo } = useAtomValue(appAtom);
  const { user } = useAtomValue(accountAtom);
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { isAccepted: hasAcceptedGDPR } = useAtomValue(gdprAtom);

  const hasIpAccess = asyncRosettaState?.result?.hasIpAccess ?? false;
  const isScmpSubscriber = asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false;
  const isSubscriptionEnabled =
    asyncRosettaState?.result?.contentServiceUserInfo?.isSubscriptionEnabled ?? false;
  const isShowSubscription = !hasIpAccess && !isScmpSubscriber && isSubscriptionEnabled;

  const bookmarkCheck = useCallback<SdkFunction["bookmark"]["check"]>(
    payload => checkIsBookmarked(payload.entityId),
    [checkIsBookmarked],
  );
  const bookmarkUpdate = useCallback<SdkFunction["bookmark"]["update"]>(
    payload =>
      bookmarkAction(payload.action, payload.entityId, { ga4Events: payload.trackings.ga4Events }),
    [bookmarkAction],
  );
  const handleCheckIsFollowed = useCallback<SdkFunction["handleCheckIsFollowed"]>(
    payload => checkIsFollowed(payload.followType, payload.entityId),
    [checkIsFollowed],
  );
  const handleFollow = useCallback<SdkFunction["handleFollow"]>(
    async payload =>
      followAction(payload.action, payload.followType, payload.entityIds, payload?.callback),
    [followAction],
  );

  const handleOpenLoginDialog = useCallback<SdkFunction["handleOpenLoginDialog"]>(
    ({ description, destination, ga4CustomParameter, title }) => {
      openLoginDialog({ description, destination, ga4CustomParameter, title });
    },
    [openLoginDialog],
  );
  const handleRemoveRouteQuery = useCallback<SdkFunction["handleRemoveRouteQuery"]>(
    async sanitizedKeys => {
      await replace(
        sanitizedKeys,
        { pathname, query },
        {
          shallow: true,
        },
      );
    },
    [pathname, query, replace],
  );
  const handleRoutePush = useCallback<SdkFunction["handleRoutePush"]>(
    async (url: string, extraOptions = {}) => {
      await handleRoutePush_(url, extraOptions);
    },
    [handleRoutePush_],
  );
  const handleTrackGA4 = useCallback<SdkFunction["handleTrackGA4"]>(payload => {
    if (!payload) return;
    sendGA4Tracking(payload, { untyped: true });
  }, []);
  const handleTrackGTM = useCallback<SdkFunction["handleTrackGTM"]>(payload => {
    if (!payload) return;
    sendGTMTracking(payload, { untyped: true });
  }, []);

  const handleTrackMirror = useCallback<SdkFunction["handleTrackMirror"]>(payload => {
    if (!payload) return;
    sendMirrorCustomEvent(payload, { untyped: true });
  }, []);

  const homeAsPath = useGetHomeAsPath();

  const hasWhyRegisterPPTOHomepagePositionIntersected =
    useHasWhyRegisterPPTOHomepagePositionIntersected();

  const value = useMemo<SdkFunction>(
    () => ({
      bookmark: {
        check: bookmarkCheck,
        update: bookmarkUpdate,
      },
      config: {
        accounts: {
          authCallbackUrl: config.account.authCallbackUrl,
          host: config.account.scmpAccountHost,
          registerCallbackUrl: config.account.registerCallbackUrl,
        },
        corporateSubscriber: {
          host: config.corporateSubscriber.host,
        },
        environment: "dev",
        facebook: {
          appId: config.social.facebook.appId,
          likeUrl: config.social.facebook.likeUrl,
        },
        graphql: {
          apiKey: config.graphql.contentService.apiKey,
          host: config.graphql.contentService.host,
        },
        newsletter: {
          globalImpact: newsletter.globalImpact.entityId,
          lunar: newsletter.lunar.entityId,
          postMag: newsletter.postMag.entityId,
          thisWeekInAsia: newsletter.thisWeekInAsia.entityId,
        },
        notification: {
          host: "",
        },
        recaptcha: {
          siteKey: config.account.recaptchaSiteKey,
        },
        siteUrl: config.general.siteUrl,
        subscribe: {
          host: config.rosetta.subscribeHost,
        },
        twitter: {
          accountName: config.social.twitter.scmp.accountName,
        },
      },
      currentArticleId,
      currentArticleUuid,
      currentPageType,
      currentRoute: asPath,
      edition,
      environment,
      handleCheckIsFollowed,
      handleFollow,
      handleOpenLoginDialog,
      handleRemoveRouteQuery,
      handleRoutePush,
      handleTrackGA4,
      handleTrackGTM,
      handleTrackMirror,
      hasAcceptedGDPR,
      hasEnoughFollowedItems,
      hasWhyRegisterPPTOHomepagePositionIntersected,
      homeAsPath,
      initialHasEnoughFollowedItems,
      isShowSubscription,
      rosettaInstance: asyncRosettaState?.result?.instance,
      router,
      user: user ?? undefined,
      userCountry: navigatorInfo?.country,
      userTypeIds,
    }),
    [
      asPath,
      asyncRosettaState?.result?.instance,
      bookmarkCheck,
      bookmarkUpdate,
      currentArticleId,
      currentArticleUuid,
      currentPageType,
      edition,
      environment,
      handleCheckIsFollowed,
      handleFollow,
      handleOpenLoginDialog,
      handleRemoveRouteQuery,
      handleRoutePush,
      handleTrackGA4,
      handleTrackGTM,
      handleTrackMirror,
      hasAcceptedGDPR,
      hasEnoughFollowedItems,
      hasWhyRegisterPPTOHomepagePositionIntersected,
      homeAsPath,
      initialHasEnoughFollowedItems,
      isShowSubscription,
      navigatorInfo?.country,
      router,
      user,
      userTypeIds,
    ],
  );

  return value;
};
